var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Vector3 } from "flavr-math";
import { RemoveSubModelMessage, ToolInfoFacet, SetToolViewerVisibilityMessage, ToolViewerActivationMessage, AddToolFromBlueprintMessage, RemoveToolFromBlueprintMessage, } from "flavr-common";
import { animationFrame$ } from "@messages/xr/AnimationFrame";
import { ControllerConnectedMessage, ControllerDisconnectedMessage } from "@messages/controller/ControllerMessages";
import { FacetHandler, FacetRemoveHandler, MessageHandler, ModelEventHandler, ModelEventType, NewModule, PresentationMode } from "@modules/module";
import { inputActionService } from "@modules/inputs/input/InputActionService";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { modelPool } from "@modules/model";
import { preferencesService } from "@modules/preferences/PreferencesService";
import { ToolBelt } from "./toolBelt/ToolBelt";
const TOOL_BELT_SHOW_LOOK_DOWN_ANGLE_DEG = 45;
const TOOL_BELT_HIDE_LOOK_DOWN_ANGLE_DEG = 10;
const RAD_TO_DEG = 180 / Math.PI;
/**
 * Manages the tool belt style viewer that exposes room tools and blueprint-based utilities to the local user.
 *
 * Facets:
 * - `ToolInfoFacet` - Provides per-room tool metadata that should be shown in the tool viewer.
 *
 * Messages:
 * - `SetToolViewerVisibilityMessage` - Toggles whether the tool viewer is shown.
 * - `ToolViewerActivationMessage` - Enables or disables the tool viewer feature itself.
 * - `AddToolFromBlueprintMessage` - Adds a blueprint-based tool to the viewer.
 * - `RemoveToolFromBlueprintMessage` - Removes a blueprint-based tool from the viewer.
 * - `ControllerConnectedMessage` - Adds tracked controller state that the viewer uses.
 * - `ControllerDisconnectedMessage` - Removes tracked controller state that the viewer uses.
 * - `RemoveSubModelMessage` - Removes stale tool entries when the corresponding model disappears.
 *
 * Events:
 * - `ModelEventType.MODEL_DESTROYED` - Removes stale tool entries when the corresponding model is destroyed.
 */
export class ToolViewerModule extends NewModule {
    /**
     * Creates the tool belt, registers the built-in blueprint tools (distance meter, foto camera, annotation gun),
     * and wires up the toggle-visibility input action and the `activateToolViewerBelt` preference.
     */
    constructor() {
        super("ToolViewerModule");
        this.rooms = new Map();
        this.controllerRayDirectionMap = new Map();
        this.isActive = true;
        this.isVR = false;
        this.isUpdatingAutoVisibility = false;
        /**
         * Belt should be below camera in y-direction and only rotated around the y-axis
         */
        this.handleMovementUpdate = () => {
            this.toolBelt.updateTransform();
            void this.updateVRToolBeltVisibility();
        };
        this.toolBelt = new ToolBelt();
        const defaultTools = [
            {
                name: "DistanceMeter",
                label: "Distance Meter",
                fileId: "EC8D72EF-7FF1-4FE9-9514-C34DF8AA9CD8"
            },
            {
                name: "FotoCamera",
                label: "Foto Camera",
                fileId: "27CF4C67-6951-4C11-86EE-DD55A1E6DC14"
            },
            {
                name: "AnnotationGun",
                label: "Annotations Gerät",
                fileId: "513182DE-6201-46D6-8534-1F0A990BB110"
            }
        ];
        defaultTools.forEach(toolInfo => {
            this.toolBelt.addBlueprintTool(toolInfo.name, toolInfo.label, toolInfo.fileId);
        });
        inputActionService.subscribeAction("toggleToolViewer", () => {
            if (!this.isActive)
                return;
            void this.toolBelt.toggleVisibility();
        });
        preferencesService.onPreferenceChanged.add((key, newValue) => __awaiter(this, void 0, void 0, function* () {
            if (key !== "activateToolViewerBelt")
                return false;
            this.isActive = Boolean(newValue).valueOf();
            if (!this.isActive)
                this.toolBelt.hide();
            return true;
        }));
    }
    /**
     * Restores the persisted activation state and starts the movement update callback that keeps the tool belt aligned with the player.
     */
    onInit() {
        this.isActive = Boolean(preferencesService.getPreferredValue("activateToolViewerBelt")).valueOf();
        animationFrame$.onMovementUpdate$.add(this.handleMovementUpdate);
    }
    /**
     * Switches the viewer between desktop and VR behaviour when the presentation mode changes, using the provided mode to select the correct belt interaction style.
     */
    onPresentationModeChanged(presentationMode, _viewPosition, _viewDirection) {
        this.isVR = presentationMode === PresentationMode.VR;
        this.toolBelt.switchMode(this.isVR);
    }
    updateVRToolBeltVisibility() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isActive || !this.isVR || this.isUpdatingAutoVisibility) {
                return;
            }
            const lookDownAngleDeg = Math.max(0, -Math.asin(localPlayerContainer.cameraWorldViewDirection.y) * RAD_TO_DEG);
            if (!this.toolBelt.isActive() && lookDownAngleDeg >= TOOL_BELT_SHOW_LOOK_DOWN_ANGLE_DEG) {
                this.isUpdatingAutoVisibility = true;
                try {
                    yield this.toolBelt.show();
                    if (!this.isActive || !this.isVR) {
                        this.toolBelt.hide();
                    }
                }
                finally {
                    this.isUpdatingAutoVisibility = false;
                }
                return;
            }
            if (this.toolBelt.isActive() && lookDownAngleDeg <= TOOL_BELT_HIDE_LOOK_DOWN_ANGLE_DEG) {
                this.toolBelt.hide();
            }
        });
    }
    /// Handler methods ///
    handleToolInfoFacet(uid, facet, meta) {
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return;
        const roomId = model.getRoom();
        if (roomId === undefined)
            return;
        let room = this.rooms.get(roomId.getUID());
        if (room == null) {
            room = {
                toolInfos: new Map()
            };
            this.rooms.set(roomId.getUID(), room);
        }
        room.toolInfos.set(uid, {
            createdTime: meta.timeStamp,
            facet
        });
    }
    handleRemoveToolInfoFacet(uid, _facetType, meta) {
        this.deleteToolInfoFacet(uid, meta.timeStamp);
    }
    handleRemoveSubModelMessage(msg, meta) {
        this.deleteToolInfoFacet(msg.uid, meta.timeStamp);
    }
    handleModelDestroyed(model, _destroyPermanently, meta) {
        this.deleteToolInfoFacet(model.getUID(), meta.timeStamp);
    }
    handleSetToolViewerVisibilityMessage(msg) {
        if (!this.isActive)
            return;
        if (msg.visibility) {
            void this.toolBelt.show();
        }
        else {
            this.toolBelt.hide();
        }
    }
    handleAddToolFromBlueprintMessage(msg) {
        this.toolBelt.addBlueprintTool(msg.name, msg.description, msg.fileId);
    }
    handleControllerConnectedMessage(msg) {
        this.controllerRayDirectionMap.set(msg.uid, new Vector3().fromArray(msg.rayDirection).normalize());
    }
    handleControllerDisconnectedMessage(msg) {
        this.controllerRayDirectionMap.delete(msg.uid);
    }
    handleRemoveToolFromBlueprintMessage(msg) {
        this.toolBelt.removeBlueprintTool(msg.name, msg.fileId);
    }
    handleToolViewerActivationMessage(msg) {
        this.isActive = msg.active;
    }
    /// Room helper methods ///
    deleteToolInfoFacet(uid, timestamp) {
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return;
        const roomId = model.getRoom();
        if (roomId === undefined)
            return;
        const room = this.rooms.get(roomId.getUID());
        if (room === undefined)
            return;
        const info = room.toolInfos.get(uid);
        if (info == null)
            return;
        if (info.createdTime > timestamp)
            return;
        room.toolInfos.delete(uid);
        if (room.toolInfos.size === 0) {
            this.rooms.delete(roomId.getUID());
        }
    }
}
__decorate([
    FacetHandler(ToolInfoFacet)
], ToolViewerModule.prototype, "handleToolInfoFacet", null);
__decorate([
    FacetRemoveHandler(ToolInfoFacet)
], ToolViewerModule.prototype, "handleRemoveToolInfoFacet", null);
__decorate([
    MessageHandler(RemoveSubModelMessage)
], ToolViewerModule.prototype, "handleRemoveSubModelMessage", null);
__decorate([
    ModelEventHandler(ModelEventType.MODEL_DESTROYED)
], ToolViewerModule.prototype, "handleModelDestroyed", null);
__decorate([
    MessageHandler(SetToolViewerVisibilityMessage)
], ToolViewerModule.prototype, "handleSetToolViewerVisibilityMessage", null);
__decorate([
    MessageHandler(AddToolFromBlueprintMessage)
], ToolViewerModule.prototype, "handleAddToolFromBlueprintMessage", null);
__decorate([
    MessageHandler(ControllerConnectedMessage)
], ToolViewerModule.prototype, "handleControllerConnectedMessage", null);
__decorate([
    MessageHandler(ControllerDisconnectedMessage)
], ToolViewerModule.prototype, "handleControllerDisconnectedMessage", null);
__decorate([
    MessageHandler(RemoveToolFromBlueprintMessage)
], ToolViewerModule.prototype, "handleRemoveToolFromBlueprintMessage", null);
__decorate([
    MessageHandler(ToolViewerActivationMessage)
], ToolViewerModule.prototype, "handleToolViewerActivationMessage", null);
